/**
 * The storefront cart's SUBTOTAL composition, as one pure function.
 *
 * Extracted from StorefrontCart.vue's ``subTotal`` computed so the
 * composition can be driven directly by tests. The computed itself cannot be
 * mounted in isolation (it pulls in Vuex, Stripe and the router), and the
 * alternative -- a test harness that re-implements the arithmetic -- proves
 * only that the copy agrees with itself. Keeping ONE definition here means a
 * test that goes green is testing the code the shopper runs.
 *
 * WHAT THE SUBTOTAL MEANS. It is the running total of everything the shopper
 * is being asked to pay, in the order those components are applied:
 *
 *     items -> sales tax -> coupon -> shipping -> free item -> processing fee
 *             -> platform fee
 *
 * The processing fee was added to this list on 2026-09-14 (Jake, from a
 * storefront screenshot: "the processing fee should be above the sub-total
 * and the sub-total should include it"). It is the only component that had
 * been rendered BELOW the subtotal line and excluded from it, which reads to
 * a shopper as a charge appended after the total was struck. Tax, coupon and
 * shipping were already folded in, so this is consistency with the existing
 * convention rather than a new one.
 *
 * The PLATFORM fee joined the list on 2026-09-15, for the same reason and
 * from the same kind of evidence -- Jake drove a live checkout (order
 * AT-2026-01033) whose dialog read Items 17.02, Processing Fee 0.79,
 * Subtotal 17.81, Total 18.48. The missing 67c was the ThreadFlow platform
 * fee (3.95% of items): folded into `cartCharge`, absent from this function
 * and from every rendered row, because a 2026-07-23 decision deliberately
 * hid it from shoppers. Jake overrode that decision for the shopper-bears
 * case, because the screen must foot on the last page before a card.
 *
 * It is folded in only when the SHOPPER bears it. When the distributor
 * absorbs the fee it is not part of the shopper's charge, so including it
 * here would overstate a figure the card will not take -- and would leak
 * ThreadFlow's margin onto a customer's screen.
 *
 * WHAT THIS IS NOT. This is the DISPLAYED subtotal, not the charged amount.
 * The authoritative charge is ``cartCharge`` in StorefrontCart.vue, which
 * composes its own figure (and which the server independently recomputes at
 * checkout). This function must not become a second definition of what the
 * shopper is charged -- in particular, it takes the processing fee as an
 * argument rather than recomputing it from a rate, so the figure folded in
 * here is always the identical figure rendered on the fee line.
 */
import currency from "currency.js";

import { halfUpCents, percentOfCentsHalfUp } from "./cartChargeCents";

/**
 * @param {Object} parts
 * @param {number} parts.total             Items subtotal, in dollars.
 * @param {number} [parts.salesTax]        Computed sales tax, in dollars.
 * @param {boolean} [parts.collectSalesTax]  Store collects sales tax.
 * @param {boolean} [parts.isSalesTaxTBD]  Tax not yet resolvable.
 * @param {number} [parts.couponAmount]    Coupon magnitude (percent or dollars).
 * @param {string} [parts.couponType]      "%" or "$".
 * @param {number} [parts.shippingCost]    Shipping, in dollars.
 * @param {boolean} [parts.shippingEnabled]  Store charges for shipping.
 * @param {number} [parts.freeItemDiscount]  Highest-priced-item-free
 *                                         discount, in dollars. Subtracted,
 *                                         clamped at zero.
 * @param {number} [parts.processingFee]   Card processing fee + app fee, in
 *                                         dollars. MUST be the same figure
 *                                         the fee line displays.
 * @param {boolean} [parts.collectProcessingFee]  Store passes the fee on.
 * @param {boolean} [parts.isProcessingFeeTBD]    Fee not yet resolvable.
 * @param {number} [parts.platformFee]     ThreadFlow platform fee, in
 *                                         dollars. MUST be the same figure
 *                                         the Service Fee line displays.
 * @param {boolean} [parts.shopperBearsPlatformFee]  The shopper pays it.
 *                                         Defaults FALSE, so a caller that
 *                                         has not been updated keeps the
 *                                         figure it had rather than
 *                                         inventing a charge.
 * @returns {number} the subtotal in dollars.
 */
export function computeCartSubTotal({
  total,
  salesTax = 0,
  collectSalesTax = false,
  isSalesTaxTBD = true,
  couponAmount = 0,
  couponType = "%",
  shippingCost = 0,
  shippingEnabled = false,
  freeItemDiscount = 0,
  processingFee = 0,
  collectProcessingFee = false,
  isProcessingFeeTBD = false,
  platformFee = 0,
  shopperBearsPlatformFee = false,
}) {
  // Work in integer cents throughout -- the original computed multiplied by
  // 100 up front for exactly this reason ("fix 0.01 issue").
  //
  // Tax, coupon and shipping are whole cents ROUND_HALF_UP, the server's
  // one rule (#481, gate round 3; cartChargeCents.js), so this subtotal
  // and the charge agree with the server's to the cent.
  let subTotal = halfUpCents(total);

  if (collectSalesTax && !isSalesTaxTBD) {
    subTotal += halfUpCents(salesTax);
  }

  if (couponAmount > 0) {
    if (couponType === "%") {
      subTotal -= percentOfCentsHalfUp(Math.max(subTotal, 0), Math.min(couponAmount, 100));
    }
    if (couponType === "$") {
      subTotal -= Math.min(halfUpCents(couponAmount), Math.max(subTotal, 0));
    }
  }

  if (shippingEnabled && shippingCost) {
    subTotal += halfUpCents(shippingCost);
  }

  // Highest-priced-item-free (https://trello.com/c/T756dpsG). POSITION IS
  // LOAD-BEARING: after tax/coupon/shipping and BEFORE the processing fee,
  // mirroring both cartCharge (StorefrontCart.vue:765-771) and the server's
  // order_money.py. Moving it either side of the fee block changes the fee
  // the two sides compute and 409s the checkout.
  //
  // Clamped at the running subtotal, exactly as the charge clamps at the
  // running charge. Without the clamp a cart whose free item outweighs
  // everything else renders a NEGATIVE subtotal above a zero total, and the
  // two figures stop reconciling at the worst possible moment.
  //
  // Omitting this was the bug Phil Fry reported on 2026-09-15: the discount
  // reached the Total and not the Subtotal, so the free-item line rendered
  // between them read as an arithmetic error on the shopper's own screen.
  if (freeItemDiscount > 0) {
    subTotal -= Math.min(freeItemDiscount * 100, Math.max(subTotal, 0));
  }

  // The processing fee, folded in LAST -- it is computed downstream of
  // everything above it in cartCharge, so adding it anywhere earlier would
  // make the subtotal a figure no other surface agrees with.
  //
  // Skipped while the fee is TBD. Adding an unknown is not defensible, and
  // silently adding zero is worse than omitting it: the subtotal would read
  // as final while a fee is still pending. The fee ROW renders "TBD" in that
  // state, so the shopper can see why the subtotal excludes it.
  //
  // `currency(...).intValue` rather than `fee * 100` because the fee is a
  // computed figure with cent-level precision -- multiplying a float by 100
  // is what produces a 14.059999999999999 subtotal.
  if (collectProcessingFee && !isProcessingFeeTBD && processingFee) {
    subTotal += currency(processingFee).intValue;
  }

  // The ThreadFlow platform fee, LAST -- matching cartCharge, which adds it
  // after the Stripe/app-fee block so the processing fee is never computed
  // on top of it.
  //
  // Gated on the BEARER, not on the fee being non-zero. Only the shopper's
  // own charge belongs in the shopper's subtotal; when the distributor
  // absorbs the fee it is settled as a Stripe application fee out of their
  // proceeds and never touches this card.
  //
  // `currency(...).intValue` rather than `fee * 100`, for the reason the fee
  // above uses it: the figure arrives as `total * rate` and a float times
  // 100 produces 395.00000000000006 cents.
  if (shopperBearsPlatformFee && platformFee) {
    subTotal += currency(platformFee).intValue;
  }

  return subTotal / 100;
}
